import type { Collections } from '@nuxt/content'
import {
  PERSON_CATEGORY_LABELS,
  PERSON_CATEGORY_ORDER,
  TEAM_MEMBER_ROLE_ORDER,
  type PersonCategory,
} from '~/constants/domain'
import type { PersonInterface } from '~/interfaces/person'
import { toPersonInterface } from '~/utils/person'

type PersonRecord = Collections['persons']

export interface PeopleSection {
  name: string
  people: PersonInterface[]
}

function toPeopleReadModel(record: PersonRecord): PersonInterface {
  return JSON.parse(
    JSON.stringify(toPersonInterface(record)),
  ) as PersonInterface
}

function comparePeopleByName(
  left: PersonInterface,
  right: PersonInterface,
): number {
  const lastNameComparison = left.name.last.localeCompare(right.name.last)

  if (lastNameComparison !== 0) {
    return lastNameComparison
  }

  return left.name.first.localeCompare(right.name.first)
}

function compareTeamMembers(
  left: PersonInterface,
  right: PersonInterface,
): number {
  const leftRoleOrder =
    TEAM_MEMBER_ROLE_ORDER[
      left.en.role as keyof typeof TEAM_MEMBER_ROLE_ORDER
    ] ?? Number.MAX_SAFE_INTEGER
  const rightRoleOrder =
    TEAM_MEMBER_ROLE_ORDER[
      right.en.role as keyof typeof TEAM_MEMBER_ROLE_ORDER
    ] ?? Number.MAX_SAFE_INTEGER

  if (leftRoleOrder !== rightRoleOrder) {
    return leftRoleOrder - rightRoleOrder
  }

  return comparePeopleByName(left, right)
}

export function sortPeople(
  people: PersonInterface[],
  category?: PersonCategory,
): PersonInterface[] {
  const sortedPeople = [...people]

  if (category === 'Team member') {
    return sortedPeople.sort(compareTeamMembers)
  }

  return sortedPeople.sort(comparePeopleByName)
}

export async function getAllPeople(): Promise<PersonInterface[]> {
  const records = (await queryCollection('persons').all()) as PersonRecord[]

  return sortPeople(records.map(toPeopleReadModel))
}

export async function getPersonByPath(
  personPath: string,
): Promise<PersonInterface | null> {
  const record = (await queryCollection('persons')
    .path(personPath)
    .first()) as PersonRecord | null

  return record ? toPeopleReadModel(record) : null
}

export function createPeopleSections(
  people: PersonInterface[],
): PeopleSection[] {
  return PERSON_CATEGORY_ORDER.map((category) => ({
    name: PERSON_CATEGORY_LABELS[category],
    people: sortPeople(
      people.filter((person) => person.category === category),
      category,
    ),
  })).filter((section) => section.people.length > 0)
}
